import type {
    Asset,
    AssetDelivery,
    AssetPage,
    AssetSearchRq,
    UploadPhase as ClientPhase,
    Folder,
} from "@pej/asset-client";
import type {
    AssetFolderSummary,
    AssetQuery,
    UploadPhase as FieldPhase,
    ImageAssetSearchPage,
    ImageAssetSummary,
} from "@pej/kore/components";

/**
 * The seam between the asset service's wire format and the picker family's
 * contract. Everything here is pure — no React, no network, no query client —
 * because these mappings are where the two models disagree, and a disagreement
 * is much easier to reason about as a function than as a line buried in a hook.
 *
 * The picker family (kore's `components/assets`) is presentational and
 * constructs nothing, so this module is the only place the translation happens.
 */

/**
 * Tiles render at ~94px in a 420px popover. Ask for double that so the image is
 * sharp on a retina panel, and let the rendition list decide what actually
 * satisfies it.
 */
const TILE_TARGET_WIDTH = 188;

/**
 * Preferred single source for an `<img src>`. The service emits AVIF, WebP,
 * JPEG and PNG for every rendition, but a plain `img` cannot negotiate — it
 * gets one URL — so this picks rather than lets the browser choose. WebP over
 * AVIF deliberately: the size difference is negligible at tile dimensions and
 * WebP has the longer support tail. Revisit if these ever back a `<picture>`.
 */
const PREFERRED_MEDIA_TYPES = ["image/webp", "image/jpeg", "image/png", "image/avif"] as const;

export function toFolderSummary(folder: Folder): AssetFolderSummary {
    return { id: folder.id, name: folder.displayName };
}

/**
 * Null for a non-image: every surface here is an image slot, and kore's image
 * components take only image summaries. Searches are filtered to images on the
 * wire (see {@link toSearchRequest}), so in practice this narrows a lookup by id.
 */
export function toImageAssetSummary(asset: Asset, thumbnailUrl: string | null): ImageAssetSummary | null {
    if (asset.type !== "IMAGE") return null;
    return {
        id: asset.id,
        displayName: asset.displayName,
        type: "IMAGE",
        state: asset.state,
        thumbnailUrl,
        width: asset.width,
        height: asset.height,
        folderId: asset.folderId,
    };
}

export function toSearchPage(page: AssetPage, thumbnails: ReadonlyMap<string, string | null>): ImageAssetSearchPage {
    return {
        items: page.items.flatMap((asset) => toImageAssetSummary(asset, thumbnails.get(asset.id) ?? null) ?? []),
        totalCount: page.totalCount,
        nextPageToken: page.nextPageToken ?? null,
    };
}

export function toSearchRequest(query: AssetQuery, pageSize: number): AssetSearchRq {
    const text = query.text.trim();
    return {
        // Omitted rather than sent empty: an empty query is a filter that
        // matches nothing on some backends and everything on others, and we
        // want "no text entered" to mean "no text predicate".
        query: text ? { value: text } : null,
        pageSize,
        pageToken: query.pageToken,
        // The service defaults to ascending id, which is arbitrary from an
        // operator's view and would bury a just-uploaded asset below the fold.
        // Recency is the best available predictor of what someone is looking
        // for (spec §11).
        orderBy: { field: "CreatedAt", order: "Desc" },
        filters: { ...toFolderFilter(query.scope), types: { type: "Eq", values: ["IMAGE"] } },
    };
}

export function toFolderFilter(scope: AssetQuery["scope"]): AssetSearchRq["filters"] {
    switch (scope.mode) {
        case "all":
            // No predicate at all. Sending an empty value set would ask for
            // "folder id in {}", which matches nothing.
            return {};
        case "unsorted":
            // `NullableIdOp` is what makes this expressible: null is a value in
            // the set, not the absence of one.
            return { folderIds: { type: "Eq", values: [null] } };
        case "folder":
            return { folderIds: { type: "Eq", values: [scope.folderId] } };
    }
}

/**
 * Picks one rendition URL for a tile, or null when nothing is renderable yet.
 *
 * Chooses by measured width rather than by preset name: the manifest reports
 * what each rendition will actually be, and preset names are a versioned list
 * this consumer should not need to track. Smallest rendition that covers the
 * target wins; if none does, the largest available is better than nothing.
 *
 * Never falls back to `original` — that is the uploaded file, potentially many
 * megabytes, and a grid of eight originals is the difference between a picker
 * that opens and one that stalls.
 */
export function pickThumbnailUrl(manifest: AssetDelivery, targetWidth: number = TILE_TARGET_WIDTH): string | null {
    const usable = manifest.renditions.filter((rendition) => rendition.sources.length > 0);
    if (usable.length === 0) return null;

    const covering = usable.filter((rendition) => rendition.width >= targetWidth);
    const chosen = covering.length
        ? covering.reduce((best, r) => (r.width < best.width ? r : best))
        : usable.reduce((best, r) => (r.width > best.width ? r : best));

    return pickSource(chosen);
}

/**
 * The one preset that returns the whole frame.
 *
 * Every other preset crops to a fixed aspect with smart gravity, so the size it
 * reports is the size of a *crop*. `CONTAIN` alone is bounds-only — the service
 * scales the whole image to fit its 1536px box and reports what came out.
 */
const ARTWORK_PRESET = "CONTAIN";

/**
 * Picks the rendition to place as template artwork, or null if there is none.
 *
 * Keys on the preset name, which {@link pickThumbnailUrl} deliberately refuses
 * to do — and the difference is not inconsistency. A tile only cares how big the
 * image is, so choosing by measured width keeps it clear of a versioned name
 * list. Artwork cares about *geometry*: the caller applies its own cover-crop or
 * contain-fit, which is only correct given the whole frame. Choosing by width
 * here would happily return a square smart-crop for a wide logo and lop off its
 * ends — and it would look plausible enough in the preview to ship.
 *
 * Null when the preset is absent rather than falling back to a cropping
 * rendition: no artwork is a better answer than silently mis-cropped artwork.
 */
export function pickArtworkUrl(manifest: AssetDelivery): string | null {
    const rendition = manifest.renditions.find((r) => r.preset === ARTWORK_PRESET && r.sources.length > 0);
    return rendition ? pickSource(rendition) : null;
}

function pickSource(rendition: AssetDelivery["renditions"][number]): string | null {
    for (const mediaType of PREFERRED_MEDIA_TYPES) {
        const source = rendition.sources.find((s) => s.mediaType === mediaType);
        if (source) return source.url;
    }
    return rendition.sources[0]?.url ?? null;
}

/**
 * Projects the client's upload phase onto the one `AssetField` renders.
 *
 * The two unions are deliberately not the same shape, and neither is wrong:
 *
 * - `hashing` has no field counterpart. It reads as a transfer that has not
 *   moved yet — a busy bar at 0%. Mapping it to `checking` would claim the
 *   bytes had already arrived.
 * - `ready` is not a phase the field renders; it is the field taking a value.
 *   The host sets that value and the phase returns to idle.
 * - `idle` is not something an upload can emit — it is the absence of one,
 *   which is why null maps to it.
 *
 * The field's `IMAGE_DIMENSIONS_EXCEEDED` copy interpolates a service-supplied
 * `detail`, but no surface carries one: not this union, not `AssetRs`. That
 * sentence renders its fallback. Left as-is rather than invented here — putting
 * a number in front of an operator that no service produced is worse than a
 * general sentence.
 */
export function toFieldPhase(phase: ClientPhase | null, totalBytes: number): FieldPhase {
    if (!phase) return { phase: "idle" };
    switch (phase.phase) {
        case "hashing":
            return { phase: "transferring", fileName: phase.fileName, sentBytes: 0, totalBytes };
        case "transferring":
            return {
                phase: "transferring",
                fileName: phase.fileName,
                sentBytes: phase.sentBytes,
                totalBytes: phase.totalBytes,
            };
        case "checking":
            return { phase: "checking", fileName: phase.fileName };
        case "rejected":
            return { phase: "rejected", fileName: phase.fileName, reason: phase.reason };
        case "ready":
            return { phase: "idle" };
    }
}
